import React, { useState, useMemo, useEffect } from 'react';
import { GenericTable } from '@shared/components/GenericTable';
import { TableAction, TableColumn } from '@shared/types/table';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormField, FormService } from '@shared/services/FormServices';
import { FIELD_CLASSES, PAGE_SIZE } from '@shared/utils/constants';
import { Upload, PlusCircle, Eye } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { useMeterReadings, useMeterRunDetail } from '../../../hook';
import { AddReadDialog } from '../dialogs/AddReadDialog';

export interface ReadItem {
  jobCard: string;
  resourceId: string;
  serialNo: string;
  utilityService: string;
  type: string;
  subType: string;
  condition: string;
  currentRead: string;
  readDate: string;
  status: string;
  cycleID?: string | number;
  meterId?: string | number;
  lastReading?: string;
  lastReadingDate?: string;
}

export interface ReadsFilters {
  utilityService?: string;
  type?: string;
  subType?: string;
  condition?: string;
  status?: string;
}

interface ReadsFilterPanelProps {
  currentFilters: ReadsFilters;
  onApplyFilters: (filters: ReadsFilters) => void;
  onResetFilters: () => void;
}

export function ReadsFilterPanel({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}: ReadsFilterPanelProps) {
  const formFields: FormField[] = [
    {
      name: 'utilityService',
      label: 'Utility Service',
      type: 'select',
      required: false,
      placeholder: 'Select utility service',
      options: [
        { label: 'All Services', value: 'all' },
        { label: 'Gas', value: 'Gas' },
        { label: 'Water', value: 'Water' },
        { label: 'Electricity', value: 'Electricity' },
      ],
      className: 'col-span-1',
    },
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      required: false,
      placeholder: 'Select type',
      options: [
        { label: 'All Types', value: 'all' },
        { label: 'Meter', value: 'Meter' },
        { label: 'Gate Valve', value: 'Gate Valve' },
        { label: 'Transformer', value: 'Transformer' },
      ],
      className: 'col-span-1',
    },
    {
      name: 'subType',
      label: 'Sub Type',
      type: 'select',
      required: false,
      placeholder: 'Select sub type',
      options: [
        { label: 'All Sub Types', value: 'all' },
        { label: 'Manual Meter', value: 'Manual Meter' },
        { label: 'Smart Meter', value: 'Smart Meter' },
        { label: 'Photo Meter', value: 'Photo Meter' },
        { label: 'AMI Meter', value: 'AMI Meter' },
      ],
      className: 'col-span-1',
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: false,
      placeholder: 'Select condition',
      options: [
        { label: 'All Conditions', value: 'all' },
        { label: 'Good', value: 'Good' },
        { label: 'Fair', value: 'Fair' },
        { label: 'Unknown', value: 'Unknown' },
        { label: 'Damaged', value: 'Damaged' },
      ],
      className: 'col-span-1',
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: false,
      placeholder: 'Select status',
      options: [
        { label: 'All Statuses', value: 'all' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Completed', value: 'Completed' },
      ],
      className: 'col-span-1',
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: 'onChange',
    defaultValues: {
      utilityService: currentFilters.utilityService || 'all',
      type: currentFilters.type || 'all',
      subType: currentFilters.subType || 'all',
      condition: currentFilters.condition || 'all',
      status: currentFilters.status || 'all',
    },
  });

  const onSubmit = (data: ReadsFilters) => {
    const cleanedFilters: ReadsFilters = {};
    if (data.utilityService && data.utilityService !== 'all') cleanedFilters.utilityService = data.utilityService;
    if (data.type && data.type !== 'all') cleanedFilters.type = data.type;
    if (data.subType && data.subType !== 'all') cleanedFilters.subType = data.subType;
    if (data.condition && data.condition !== 'all') cleanedFilters.condition = data.condition;
    if (data.status && data.status !== 'all') cleanedFilters.status = data.status;

    onApplyFilters(cleanedFilters);
  };

  const handleReset = () => {
    form.reset({
      utilityService: 'all',
      type: 'all',
      subType: 'all',
      condition: 'all',
      status: 'all',
    });
    onResetFilters();
  };

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-2 pt-2 border-t mt-4 col-span-full">
      <Button type="button" variant="outline" size="sm" onClick={handleReset}>
        Reset
      </Button>
      <Button type="submit" size="sm">
        Apply Filters
      </Button>
    </div>
  );

  return (
    <DynamicForm
      fields={formFields}
      form={form}
      onSubmit={onSubmit}
      gridColumns={5}
      config={{
        showSubmitButton: false,
        classes: FIELD_CLASSES,
      }}
      renderCustomFooter={renderCustomFooter}
    />
  );
}

export const ReadsTab: React.FC = () => {
  const { runId } = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ReadsFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ReadItem | null>(null);
  const [showAddReadDialog, setShowAddReadDialog] = useState(false);

  const { data: meterReadingsData, isLoading, refetch } = useMeterReadings(
    page,
    remoteUtilityId,
    runId,
    PAGE_SIZE,
    searchQuery,
    filters.utilityService,
    filters.type,
    filters.subType,
    filters.condition,
    filters.status
  );
  const { data: meterRunDetail } = useMeterRunDetail(runId, remoteUtilityId);
  const formatId = meterRunDetail?.meterSchedule?.meterCycle?.readFormat;

  const displayReadItems: ReadItem[] = useMemo(() => {
    const rawList = Array.isArray(meterReadingsData?.results) ? meterReadingsData.results : [];

    if (rawList && rawList.length > 0) {
      return rawList.map((item: any): ReadItem => ({
        jobCard: item.jobCard?.jobCardNo|| 'N/A',
        resourceId: item.meter?.meterNumber || 'N/A',
        serialNo: item.meter?.deviceNo || 'N/A',
        utilityService: item.productCode,
        type: item.meter?.resourceTypeDisplay || 'N/A',
        subType: item.meter?.resourceSubTypeDisplay || 'N/A',
        condition: item.status || 'N/A',
        currentRead: item.meterReading !== undefined && item.meterReading !== null ? item.meterReading.toString() : '—',
        readDate: item.createdDate,
        status: item.jobCard?.status || 'N/A',
        cycleID: item.cycleID || item.cycleId || item.jobCard?.meterRun?.id || item.meterRunId || item.jobCard?.meterRunId || '—',
        meterId: item.meter?.id,
        lastReading: item.meter?.lastReading ?? '',
        lastReadingDate: item.meter?.lastReadingDate ?? '',
      }));
    }
    return [];
  }, [meterReadingsData]);

  const totalItems = meterReadingsData?.count || 0;

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
      totalItems: totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalItems),
    }),
    [page, totalItems]
  );

  const handleAddRead = (row: ReadItem) => {
    setSelectedRow(row);
    setShowAddReadDialog(true);
  };

  const actions: TableAction[] = [
    {
      label: 'Add Read',
      onClick: handleAddRead,
      icon: <PlusCircle className="w-4 h-4 text-blue-600" />,
      variant: 'ghost',
    },
    {
      label: 'View',
      onClick: (row) => {
        if (row.cycleID && row.cycleID !== '—') {
          navigate(`/mx/meter-validation/photometer-validation/view/${row.cycleID}`);
        }
      },
      icon: <Eye className="w-4 h-4 text-emerald-600" />,
      variant: 'ghost',
    },
  ];

  const columns: TableColumn<ReadItem>[] = [
    {
      key: 'jobCard',
      header: 'Job Card',
      render: (val) => <span className="text-gray-700 text-xs font-medium">{val}</span>,
    },
    {
      key: 'resourceId',
      header: 'Resource ID',
    },
    {
      key: 'serialNo',
      header: 'Serial No',
      render: (val) => <span className="text-gray-600 text-xs">{val}</span>,
    },
    {
      key: 'utilityService',
      header: 'Utility Service',
      render: (val) => <span className="text-gray-700 text-xs">{val}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      render: (val) => <span className="text-gray-700 text-xs">{val}</span>,
    },
    {
      key: 'subType',
      header: 'Sub Type',
      render: (val) => <span className="text-gray-700 text-xs">{val}</span>,
    },
    {
      key: 'condition',
      header: 'Condition',
      render: (val) => {
        let badgeClass = 'bg-gray-100 text-gray-700 border-gray-200';
        if (val === 'Fair') badgeClass = 'bg-amber-50 text-amber-700 border-amber-200';
        if (val === 'Good') badgeClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
        if (val === 'Damaged') badgeClass = 'bg-red-50 text-red-700 border-red-200';
        return (
          <Badge variant="outline" className={`text-[11px] font-normal px-2 py-0.5 rounded-full ${badgeClass}`}>
            {val}
          </Badge>
        );
      },
    },
    {
      key: 'currentRead',
      header: 'Current Read',
      render: (val) => <span className="text-gray-700 text-xs">{val}</span>,
    },
    {
      key: 'readDate',
      header: 'Read Date',
      render: (val) => <span className="text-gray-600 text-xs">{val}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (val) => (
        <Badge
          variant="outline"
          className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${val === 'Completed'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}
        >
          {val}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-4 pt-2">
      {/* Top Bar with Import Action */}
      <div className="flex flex-wrap items-center justify-end gap-3">

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="text-xs font-medium text-gray-700 border-gray-200 h-9 flex items-center gap-1.5"
          >
            <Upload className="w-3.5 h-3.5 text-gray-500" />
            <span>Import Reads</span>
          </Button>
        </div>
      </div>

      {/* GenericTable with integrated FilterPanel & Actions */}
      <GenericTable
        data={displayReadItems}
        columns={columns}
        rowKey="jobCard"
        loading={isLoading}
        actions={actions}
        actionsType="icons"
        pagination={pagination}
        onPageChange={(p) => setPage(p)}
        showFilters={showFilters}
        onShowFiltersChange={setShowFilters}
        filterPanel={
          <ReadsFilterPanel
            currentFilters={filters}
            onApplyFilters={(newFilters) => {
              setFilters(newFilters);
              setPage(1);
            }}
            onResetFilters={() => {
              setFilters({});
              setPage(1);
            }}
          />
        }
        searchPlaceholder="Search by Job Card, Resource ID, Serial No..."
        onSearch={(term) => {
          setSearchQuery(term);
          setPage(1);
        }}
        emptyMessage="No read records found."
      />

      <AddReadDialog
        open={showAddReadDialog}
        setOpen={setShowAddReadDialog}
        row={selectedRow}
        remoteUtilityId={remoteUtilityId}
        formatId={formatId}
        refetch={refetch}
      />
    </div>
  );
};
export default ReadsTab;
